import "./userAddresses.css";
import "leaflet/dist/leaflet.css";

import { CircleMarker, MapContainer, TileLayer, useMap, useMapEvents } from "react-leaflet";
import { DEFAULT_COUNTRY, getCountryCodeOptions } from "../../../service/service";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { API_URL } from "../../../../env";

const DEFAULT_CENTER = { lat: 28.6139, lng: 77.209 };

const MapInteraction = ({ center, onPick }) => {
  const map = useMap();

  useEffect(() => {
    map.setView(center, map.getZoom(), { animate: true });
  }, [center, map]);

  useMapEvents({
    click(event) {
      onPick({ lat: event.latlng.lat, lng: event.latlng.lng });
    }
  });

  return null;
};

const emptyForm = {
  addressName: "Home",
  address: "",
  city: "",
  state: "",
  country: "India",
  zip: "",
  recipientPhoneNumber: "",
  recipientPhoneCountryCode: DEFAULT_COUNTRY.value,
  isPrimary: false,
  countryCode: "IN",
  stateCode: "",
  cityCode: "",
  areaId: "AREA001",
  commpleteAddress: ""
};

const STATE_CODE_MAP = {
  "Uttar Pradesh": "UP"
};

const COUNTRY_CODE_MAP = {
  India: "IN"
};

const DEFAULT_PHONE_CODE_OPTIONS = [{ text: DEFAULT_COUNTRY.text, value: DEFAULT_COUNTRY.value }];

const UserAddresses = () => {
  const [addresses, setAddresses] = useState([]);
  const [phoneCodeOptions, setPhoneCodeOptions] = useState(DEFAULT_PHONE_CODE_OPTIONS);

  const [selectedCoords, setSelectedCoords] = useState(() => {
    const saved = JSON.parse(localStorage.getItem("vbLastCoords") || "null");
    return saved?.lat && saved?.lng ? saved : DEFAULT_CENTER;
  });

  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const searchRequestIdRef = useRef(0);
  const userId = (sessionStorage.getItem("userId") || "").replaceAll('"', "").trim();
  const token = sessionStorage.getItem("token") || "";

  const sortedAddresses = useMemo(
    () => [...addresses].sort((a, b) => Number(Boolean(b.isPrimary)) - Number(Boolean(a.isPrimary))),
    [addresses]
  );

  const updateForm = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const normalizeCodeFromText = useCallback((value, fallback = "") => {
    const words = String(value || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!words.length) {
      return fallback;
    }
    return words.map((word) => word[0]).join("").toUpperCase();
  }, []);

  const buildCityCode = useCallback((city = "") => {
    const compact = city.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    if (!compact) {
      return "";
    }
    return `${compact.slice(0, 3)}001`;
  }, []);

  const buildCompleteAddress = useCallback((addressPayload) => {
    return [addressPayload.address, addressPayload.city, addressPayload.state, addressPayload.country]
      .filter(Boolean)
      .join(", ");
  }, []);

  const normalizeAddressItem = useCallback((item, index) => {
    const resolvedAddressId = item?.addressId || item?.id || item?._id || item?.internalKey || `addr-${Date.now()}-${index}`;
    const addressName = item?.addressName || `Address ${index + 1}`;
    const address = item?.address || "";
    const city = item?.city || "";
    const state = item?.state || "";
    const country = item?.country || "India";
    const zip = item?.zip || "";
    const recipientPhoneNumber = item?.recipientPhoneNumber || "";
    const recipientPhoneCountryCode = item?.recipientPhoneCountryCode || DEFAULT_COUNTRY.value;
    const countryCode = item?.countryCode || COUNTRY_CODE_MAP[country] || "IN";
    const stateCode = item?.stateCode || STATE_CODE_MAP[state] || normalizeCodeFromText(state, "");
    const cityCode = item?.cityCode || buildCityCode(city);
    const areaId = item?.areaId || "AREA001";
    const commpleteAddress = item?.commpleteAddress || [address, city, state, country].filter(Boolean).join(", ");

    return {
      internalKey: resolvedAddressId,
      addressId: resolvedAddressId,
      addressName,
      address,
      city,
      state,
      country,
      zip,
      recipientPhoneNumber,
      recipientPhoneCountryCode,
      countryCode,
      stateCode,
      cityCode,
      areaId,
      commpleteAddress,
      lat: Number(item?.lat ?? 0),
      lng: Number(item?.lng ?? 0),
      isPrimary: Boolean(item?.isPrimary ?? item?.isPrimary)
    };
  }, [buildCityCode, normalizeCodeFromText]);

  const fetchSavedAddresses = useCallback(async () => {
    if (!userId) {
      setAddresses([]);
      return;
    }

    setListLoading(true);
    setListError("");

    try {
      const response = await fetch(`${API_URL}/profile/listOfAddress/${encodeURIComponent(userId)}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          env: "local",
          ...(token ? { token } : {})
        }
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || "Unable to load saved addresses.");
      }

      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.responseData)
          ? payload.responseData
          : Array.isArray(payload?.data)
            ? payload.data
            : [];

      const normalizedList = list.map((item, index) => normalizeAddressItem(item, index));
      setAddresses(normalizedList);

      if (normalizedList.length > 0) {
        const firstWithCoordinates = normalizedList.find((item) => item.lat && item.lng);
        if (firstWithCoordinates) {
          setSelectedCoords({ lat: firstWithCoordinates.lat, lng: firstWithCoordinates.lng });
        }
      }
    } catch (error) {
      setListError(error?.message || "Unable to load saved addresses.");
    } finally {
      setListLoading(false);
    }
  }, [normalizeAddressItem, token, userId]);

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setSearchError("");
    setSubmitError("");
    setSearchQuery("");
    setSearchResults([]);
    setActiveSuggestionIndex(-1);
  };

  const buildAddressPayload = useCallback(() => {
    const nextCountryCode = formData.countryCode || COUNTRY_CODE_MAP[formData.country] || "IN";
    const nextStateCode = formData.stateCode || STATE_CODE_MAP[formData.state] || normalizeCodeFromText(formData.state, "");
    const nextCityCode = formData.cityCode || buildCityCode(formData.city);

    const payload = {
      country: formData.country,
      countryCode: nextCountryCode,
      state: formData.state,
      stateCode: nextStateCode,
      city: formData.city,
      cityCode: nextCityCode,
      address: formData.address,
      areaId: formData.areaId || "AREA001",
      zip: formData.zip,
      recipientPhoneNumber: formData.recipientPhoneNumber,
      recipientPhoneCountryCode: formData.recipientPhoneCountryCode || DEFAULT_COUNTRY.value,
      lat: Number(selectedCoords.lat),
      lng: Number(selectedCoords.lng),
      addressName: formData.addressName,
      isPrimary: Boolean(formData.isPrimary),
      commpleteAddress: ""
    };

    payload.commpleteAddress = formData.commpleteAddress || buildCompleteAddress(payload);
    return payload;
  }, [buildCityCode, buildCompleteAddress, formData, normalizeCodeFromText, selectedCoords]);

  const populateAddressFields = useCallback((address = {}, fallbackLine = "") => {
    const city = address.city || address.town || address.village || address.county || "";
    const state = address.state || address.state_district || "";
    const zip = address.postcode || "";
    const country = address.country || "India";
    const line1 = [address.house_number, address.road, address.suburb, address.neighbourhood]
      .filter(Boolean)
      .join(", ") || fallbackLine;

    updateForm("address", line1);
    updateForm("city", city);
    updateForm("state", state);
    updateForm("country", country);
    updateForm("zip", zip);
    updateForm("countryCode", COUNTRY_CODE_MAP[country] || "IN");
    updateForm("stateCode", STATE_CODE_MAP[state] || normalizeCodeFromText(state, ""));
    updateForm("cityCode", buildCityCode(city));
    updateForm("areaId", "AREA001");
    updateForm("commpleteAddress", [line1, city, state, country].filter(Boolean).join(", "));
  }, [buildCityCode, normalizeCodeFromText, updateForm]);

  useEffect(() => {
    fetchSavedAddresses();
  }, [fetchSavedAddresses]);

  useEffect(() => {
    const loadPhoneCodeOptions = async () => {
      const options = await getCountryCodeOptions();
      if (options.length > 0) {
        setPhoneCodeOptions(options);
      }
    };

    loadPhoneCodeOptions();
  }, []);

  const reverseGeocode = useCallback(async (coords) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&addressdetails=1&lat=${encodeURIComponent(coords.lat)}&lon=${encodeURIComponent(coords.lng)}`,
        {
          headers: {
            Accept: "application/json"
          }
        }
      );

      const payload = await response.json();
      if (payload?.address) {
        populateAddressFields(payload.address, payload.display_name || "");
      }
    } catch (error) {
      console.error("Reverse geocoding failed", error);
    }
  }, [populateAddressFields]);

  const setPickedCoordinates = (nextCoords, options = {}) => {
    const { shouldReverseGeocode = false } = options;
    setSelectedCoords(nextCoords);
    localStorage.setItem("vbLastCoords", JSON.stringify(nextCoords));

    if (shouldReverseGeocode) {
      reverseGeocode(nextCoords);
    }
  };

  const applySearchResult = (result) => {
    const nextCoords = {
      lat: Number(result.lat),
      lng: Number(result.lon)
    };

    populateAddressFields(result.address || {}, result.display_name || "");
    setPickedCoordinates(nextCoords);
    setSearchQuery(result.display_name || "");
    setSearchResults([]);
    setSearchError("");
    setActiveSuggestionIndex(-1);
  };

  const searchPlaces = useCallback(async (rawQuery, options = {}) => {
    const { showValidationError = false } = options;
    const query = rawQuery.trim();

    if (!query) {
      setSearchResults([]);
      setSearchLoading(false);
      setSearchError(showValidationError ? "Enter address, landmark, or pincode to search." : "");
      setActiveSuggestionIndex(-1);
      return;
    }

    if (query.length < 3) {
      setSearchResults([]);
      setSearchLoading(false);
      setSearchError(showValidationError ? "Type at least 3 characters to search places." : "");
      setActiveSuggestionIndex(-1);
      return;
    }

    const requestId = searchRequestIdRef.current + 1;
    searchRequestIdRef.current = requestId;
    setSearchLoading(true);
    setSearchError("");

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=5&q=${encodeURIComponent(query)}`,
        {
          headers: {
            Accept: "application/json"
          }
        }
      );

      const payload = await response.json();
      if (requestId !== searchRequestIdRef.current) {
        return;
      }

      if (!Array.isArray(payload) || payload.length === 0) {
        setSearchResults([]);
        setSearchError("No matching address found. Try a fuller address or pincode.");
        setActiveSuggestionIndex(-1);
        return;
      }

      setSearchResults(payload);
      setActiveSuggestionIndex(0);
    } catch (error) {
      if (requestId !== searchRequestIdRef.current) {
        return;
      }

      console.error("Address search failed", error);
      setSearchResults([]);
      setSearchError("Address lookup failed. Please try again.");
      setActiveSuggestionIndex(-1);
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setSearchLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const query = searchQuery.trim();
    if (!query) {
      setSearchResults([]);
      setSearchLoading(false);
      setSearchError("");
      setActiveSuggestionIndex(-1);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      searchPlaces(query);
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [searchQuery, searchPlaces]);

  const handleAddressSearch = async (event) => {
    event.preventDefault();
    await searchPlaces(searchQuery, { showValidationError: true });
  };

  const handleSearchKeyDown = (event) => {
    if (!searchResults.length) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveSuggestionIndex((prev) => (prev + 1) % searchResults.length);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveSuggestionIndex((prev) => (prev <= 0 ? searchResults.length - 1 : prev - 1));
      return;
    }

    if (event.key === "Enter" && activeSuggestionIndex >= 0) {
      event.preventDefault();
      applySearchResult(searchResults[activeSuggestionIndex]);
      return;
    }

    if (event.key === "Escape") {
      setSearchResults([]);
      setActiveSuggestionIndex(-1);
    }
  };

  const handleMapPick = (nextCoords) => {
    setPickedCoordinates(nextCoords, { shouldReverseGeocode: true });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");

    if (!formData.addressName || !formData.address || !formData.city || !formData.state || !formData.zip) {
      return;
    }

    if (!userId) {
      setSubmitError("User id not found. Please login again.");
      return;
    }

    setSubmitLoading(true);

    try {
      const endpoint = editingId
        ? `${API_URL}/profile/updateAddress/${encodeURIComponent(userId)}/${encodeURIComponent(editingId)}`
        : `${API_URL}/profile/addAddress/${encodeURIComponent(userId)}`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          env: "local",
          ...(token ? { token } : {})
        },
        body: JSON.stringify(editingId ? buildAddressPayload() : [buildAddressPayload()])
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || "Unable to save address right now.");
      }

      await fetchSavedAddresses();
      resetForm();
    } catch (error) {
      setSubmitError(error?.message || "Unable to save address right now.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (address) => {
    setFormData({
      addressName: address.addressName || "Home",
      address: address.address || "",
      city: address.city || "",
      state: address.state || "",
      country: address.country || "India",
      zip: address.zip || "",
      recipientPhoneNumber: address.recipientPhoneNumber || "",
      recipientPhoneCountryCode: address.recipientPhoneCountryCode || DEFAULT_COUNTRY.value,
      isPrimary: Boolean(address.isPrimary),
      countryCode: address.countryCode || "IN",
      stateCode: address.stateCode || "",
      cityCode: address.cityCode || "",
      areaId: address.areaId || "AREA001",
      commpleteAddress: address.commpleteAddress || ""
    });

    setSelectedCoords({
      lat: Number(address.lat || DEFAULT_CENTER.lat),
      lng: Number(address.lng || DEFAULT_CENTER.lng)
    });
    setEditingId(address.addressId || address.internalKey);
  };

  const handleDelete = async (id) => {
    if (!userId) {
      setSubmitError("User id not found. Please login again.");
      return;
    }

    setSubmitLoading(true);
    setSubmitError("");

    try {
      const response = await fetch(`${API_URL}/profile/deleteAddress/${encodeURIComponent(userId)}/${encodeURIComponent(id)}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          env: "local",
          ...(token ? { token } : {})
        }
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || "Unable to delete address.");
      }

      if (editingId === id) {
        resetForm();
      }

      await fetchSavedAddresses();
    } catch (error) {
      setSubmitError(error?.message || "Unable to delete address.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const setAsDefault = (id) => {
    if (!userId) {
      setSubmitError("User id not found. Please login again.");
      return;
    }

    setSubmitLoading(true);
    setSubmitError("");

    fetch(`${API_URL}/profile/markAsDefaultAddress/${encodeURIComponent(userId)}/${encodeURIComponent(id)}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        env: "local",
        ...(token ? { token } : {})
      }
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(payload?.message || "Unable to mark default address.");
        }
        await fetchSavedAddresses();
      })
      .catch((error) => {
        setSubmitError(error?.message || "Unable to mark default address.");
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  return (
    <div className="ua-page">
      <section className="ua-map-card mb-2">
        <header className="ua-card-header">
          <span className="ua-card-eyebrow">Location setup</span>
          <h2>Saved addresses</h2>
          <p>
            Add and manage addresses with recipient phone details and map pin support.
          </p>
          <ul className="ua-guidance-list">
            <li>Search by landmark or pincode to autofill faster.</li>
            <li>Use labels like Home/Work for clearer delivery context.</li>
            <li>Set one default address for smoother booking checkout.</li>
          </ul>
        </header>

        <form className="ua-search-row" onSubmit={handleAddressSearch}>
          <input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search by address, area, landmark, or pincode"
            aria-autocomplete="list"
            aria-controls="ua-search-results"
          />
          <button type="submit" className="vb-btn vb-btn-primary" disabled={searchLoading}>
            {searchLoading ? "Searching..." : "Search"}
          </button>
        </form>

        {searchError && <p className="ua-search-error">{searchError}</p>}
        {submitError && <p className="ua-search-error">{submitError}</p>}

        {!!searchResults.length && (
          <div className="ua-search-results" id="ua-search-results" role="listbox">
            {searchResults.map((result, index) => (
              <button
                type="button"
                key={`${result.place_id}-${result.lat}-${result.lon}`}
                className={index === activeSuggestionIndex ? "active" : ""}
                onClick={() => applySearchResult(result)}
                onMouseEnter={() => setActiveSuggestionIndex(index)}
              >
                <strong>{result.display_name}</strong>
                <span>
                  {result.address?.postcode || "No pincode"} {result.address?.city || result.address?.town || result.address?.village || ""}
                </span>
              </button>
            ))}
          </div>
        )}

        <div className="ua-map-wrap">
          <MapContainer center={selectedCoords} zoom={15} scrollWheelZoom className="ua-leaflet-map">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <CircleMarker center={selectedCoords} radius={10} pathOptions={{ color: "#0f766e", fillColor: "#14b8a6", fillOpacity: 0.9 }} />
            <MapInteraction center={selectedCoords} onPick={handleMapPick} />
          </MapContainer>
        </div>

        <small>
          Selected coordinates: {selectedCoords.lat.toFixed(5)}, {selectedCoords.lng.toFixed(5)}
        </small>
      </section>

      <section className="ua-grid">
        <article className="ua-stats-card">
          <div>
            <small>Total addresses</small>
            <strong>{sortedAddresses.length}</strong>
          </div>
          <div>
            <small>Default address</small>
            <strong>{sortedAddresses.find((item) => item.isPrimary)?.addressName || "Not set"}</strong>
          </div>
          <div>
            <small>Map pin</small>
            <strong>{selectedCoords.lat.toFixed(2)}, {selectedCoords.lng.toFixed(2)}</strong>
          </div>
        </article>

        <form className="ua-form-card" onSubmit={handleSubmit}>
          <div className="ua-section-head">
            <h3>{editingId ? "Update address" : "Add new address"}</h3>
            <p>{editingId ? "Refine the selected address details and save the latest version." : "Create a polished address card with contact details for deliveries and visits."}</p>
          </div>

          <div className="ua-form-grid">
            <label>
              Label
              <select value={formData.addressName} onChange={(event) => updateForm("addressName", event.target.value)}>
                <option value="Home">Home</option>
                <option value="Work">Work</option>
                <option value="Other">Other</option>
              </select>
            </label>

            <label className="full">
              Address line
              <input value={formData.address} onChange={(event) => updateForm("address", event.target.value)} required />
            </label>

            <label>
              City
              <input value={formData.city} onChange={(event) => updateForm("city", event.target.value)} required />
            </label>

            <label>
              State
              <input value={formData.state} onChange={(event) => updateForm("state", event.target.value)} required />
            </label>

            <label>
              Country
              <input value={formData.country} onChange={(event) => updateForm("country", event.target.value)} required />
            </label>

            <label>
              ZIP
              <input value={formData.zip} onChange={(event) => updateForm("zip", event.target.value)} required />
            </label>

            <label>
              Recipient phone code
              <select
                value={formData.recipientPhoneCountryCode}
                onChange={(event) => updateForm("recipientPhoneCountryCode", event.target.value)}
              >
                {phoneCodeOptions.map((option) => (
                  <option key={`${option.value}-${option.text}`} value={option.value}>
                    {option.text}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Recipient phone number
              <input
                type="tel"
                inputMode="numeric"
                value={formData.recipientPhoneNumber}
                onChange={(event) => updateForm("recipientPhoneNumber", event.target.value.replace(/[^0-9]/g, ""))}
                placeholder="Enter recipient phone number"
              />
            </label>

            {/* {!editingId && (
              <label className="full ua-checkbox-row">
                <input
                  type="checkbox"
                  checked={Boolean(formData.isPrimary)}
                  onChange={(event) => updateForm("isPrimary", event.target.checked)}
                />
                Set as default address
              </label>
            )} */}
          </div>

          <div className="ua-actions">
            <button type="submit" className="vb-btn vb-btn-primary" disabled={submitLoading}>
              {submitLoading ? "Saving..." : editingId ? "Update Address" : "Add Address"}
            </button>
            {editingId && (
              <button type="button" className="vb-btn vb-btn-secondary" onClick={resetForm}>
                Cancel Update
              </button>
            )}
          </div>
        </form>

        <section className="ua-list-card">
          <div className="ua-section-head">
            <h3>Address book</h3>
            <p>Review, edit, or mark the right address as your current default.</p>
          </div>
          <div className="ua-list">
            {listError && <p className="ua-search-error">{listError}</p>}
            {listLoading && <p className="ua-empty">Loading addresses...</p>}

            {sortedAddresses.map((item) => (
              <article key={item.internalKey} className="ua-address-item">
                <div className="ua-address-head">
                  <span>{item.addressName || "Address"}</span>
                  {item.isPrimary && <strong>Default</strong>}
                </div>

                <p>{item.address}</p>
                <p>
                  {item.city}, {item.state} - {item.zip}
                </p>
                {!!item.recipientPhoneNumber && <p className="ua-phone-line">{`${item.recipientPhoneCountryCode || DEFAULT_COUNTRY.value} ${item.recipientPhoneNumber}`}</p>}
                {!!item.commpleteAddress && <p>{item.commpleteAddress}</p>}

                <div className="ua-item-actions">
                  <button type="button" className="vb-btn vb-btn-secondary" onClick={() => handleEdit(item)}>Edit</button>
                  {!item.isPrimary && (
                    <button type="button" className="vb-btn vb-btn-tertiary" onClick={() => setAsDefault(item.addressId || item.internalKey)}>Set Default</button>
                  )}
                  <button type="button" className="vb-btn vb-btn-tertiary danger" onClick={() => handleDelete(item.addressId || item.internalKey)}>
                    Delete
                  </button>
                </div>
              </article>
            ))}

            {!sortedAddresses.length && <p className="ua-empty">No addresses added yet. Add your first location.</p>}
          </div>
        </section>
      </section>
    </div>
  );
};

export default UserAddresses;
